import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import SearchIcon from "@mui/icons-material/Search";
import {
  deleteBuyerApi,
  getBuyersApi,
  getFbrProvinces,
  saveBuyerApi,
  toggleBuyerStatusApi,
  updateBuyerApi,
} from "../api";
import { useData } from "../ContextApi/ContextProvider";

const emptyBuyer = {
  buyer_business_name: "",
  buyer_ntn_cnic: "",
  buyer_sales_tax_no: "",
  buyer_strn: "",
  buyer_province: "",
  buyer_address: "",
  buyer_registration_type: "Registered",
  terms_of_sale: "",
  is_active: 1,
};

const registrationTypes = ["Registered", "Unregistered"];

const pick = (obj, keys) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
  }
  return "";
};

const normalizeBuyer = (buyer = {}) => ({
  id: pick(buyer, ["id", "buyer_id"]),
  buyer_business_name: pick(buyer, ["buyer_business_name", "buyerBusinessName"]),
  buyer_ntn_cnic: pick(buyer, ["buyer_ntn_cnic", "buyerNTNCNIC"]),
  buyer_sales_tax_no: pick(buyer, ["buyer_sales_tax_no", "buyerSalesTaxNo", "buyerSalesTaxNumber"]),
  buyer_strn: pick(buyer, ["buyer_strn", "buyerSTRN", "buyerStrn"]),
  buyer_province: pick(buyer, ["buyer_province", "buyerProvince"]),
  buyer_address: pick(buyer, ["buyer_address", "buyerAddress"]),
  buyer_registration_type: pick(buyer, ["buyer_registration_type", "buyerRegistrationType"]) || "Registered",
  terms_of_sale: pick(buyer, ["terms_of_sale", "termsOfSale"]),
  is_active: pick(buyer, ["is_active", "isActive"]) === 0 || pick(buyer, ["is_active", "isActive"]) === false ? 0 : 1,
});

const Buyers = () => {
  const { globalData, setLoader } = useData();
  const [buyers, setBuyers] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBuyer, setEditingBuyer] = useState(null);
  const [form, setForm] = useState(emptyBuyer);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const filteredBuyers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return buyers;
    return buyers.filter((buyer) =>
      [
        buyer.buyer_business_name,
        buyer.buyer_ntn_cnic,
        buyer.buyer_sales_tax_no,
        buyer.buyer_strn,
        buyer.buyer_province,
      ].some((value) => String(value || "").toLowerCase().includes(term))
    );
  }, [buyers, search]);

  const loadBuyers = async () => {
    try {
      setError("");
      setLoader(true);
      const rows = await getBuyersApi();
      setBuyers((Array.isArray(rows) ? rows : []).map(normalizeBuyer));
    } catch (err) {
      setError(err?.message || "Failed to load buyers.");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    loadBuyers();
  }, []);

  useEffect(() => {
    if (!globalData) return;
    getFbrProvinces(globalData)
      .then((rows) => setProvinces(Array.isArray(rows) ? rows : []))
      .catch(() => setProvinces([]));
  }, [globalData]);

  const openAddDialog = () => {
    setEditingBuyer(null);
    setForm({
      ...emptyBuyer,
      buyer_province: provinces[0]?.stateProvinceDesc || globalData?.province || "",
    });
    setDialogOpen(true);
  };

  const openEditDialog = (buyer) => {
    setEditingBuyer(buyer);
    setForm({ ...emptyBuyer, ...buyer });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingBuyer(null);
    setForm(emptyBuyer);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.buyer_business_name.trim()) {
      setError("Buyer business name is required.");
      return;
    }

    try {
      setError("");
      setMessage("");
      setLoader(true);
      if (editingBuyer?.id) {
        await updateBuyerApi(editingBuyer.id, form);
        setMessage("Buyer updated.");
      } else {
        await saveBuyerApi(form);
        setMessage("Buyer added.");
      }
      closeDialog();
      await loadBuyers();
    } catch (err) {
      setError(err?.message || "Failed to save buyer.");
    } finally {
      setLoader(false);
    }
  };

  const handleToggleStatus = async (buyer) => {
    try {
      setError("");
      setMessage("");
      setLoader(true);
      await toggleBuyerStatusApi(buyer.id, buyer.is_active !== 1);
      setMessage(buyer.is_active === 1 ? "Buyer marked inactive." : "Buyer marked active.");
      await loadBuyers();
    } catch (err) {
      setError(err?.message || "Failed to update buyer status.");
    } finally {
      setLoader(false);
    }
  };

  const handleDelete = async (buyer) => {
    const ok = window.confirm(`Delete ${buyer.buyer_business_name || "this buyer"}?`);
    if (!ok) return;

    try {
      setError("");
      setMessage("");
      setLoader(true);
      await deleteBuyerApi(buyer.id);
      setMessage("Buyer deleted.");
      await loadBuyers();
    } catch (err) {
      setError(err?.message || "Failed to delete buyer.");
    } finally {
      setLoader(false);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, color: "#0f172a" }}>
            Buyers
          </Typography>
          <Typography sx={{ color: "#64748b", fontWeight: 650, mt: 0.5 }}>
            Manage reusable buyer profiles for invoices.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAddDialog}
          sx={{ minHeight: 42, fontWeight: 900, textTransform: "none", borderRadius: 1.5 }}
        >
          Add Buyer
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

      <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ p: 2, borderBottom: "1px solid #e2e8f0", bgcolor: "#fff" }}>
          <TextField
            size="small"
            placeholder="Search buyers"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "#94a3b8" }} /> }}
            sx={{ maxWidth: 360, width: "100%" }}
          />
        </Box>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell sx={{ fontWeight: 900 }}>Buyer</TableCell>
                <TableCell sx={{ fontWeight: 900 }}>NTN/CNIC</TableCell>
                <TableCell sx={{ fontWeight: 900 }}>Sales Tax No</TableCell>
                <TableCell sx={{ fontWeight: 900 }}>STRN</TableCell>
                <TableCell sx={{ fontWeight: 900 }}>Province</TableCell>
                <TableCell sx={{ fontWeight: 900 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 900 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBuyers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 5, textAlign: "center", color: "#64748b", fontWeight: 700 }}>
                    No buyers found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredBuyers.map((buyer) => (
                  <TableRow key={buyer.id || `${buyer.buyer_business_name}-${buyer.buyer_ntn_cnic}`} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 850, color: "#0f172a" }}>
                        {buyer.buyer_business_name || "-"}
                      </Typography>
                      <Typography sx={{ color: "#64748b", fontSize: 12 }}>
                        {buyer.buyer_address || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>{buyer.buyer_ntn_cnic || "-"}</TableCell>
                    <TableCell>{buyer.buyer_sales_tax_no || "-"}</TableCell>
                    <TableCell>{buyer.buyer_strn || "-"}</TableCell>
                    <TableCell>{buyer.buyer_province || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={buyer.is_active === 1 ? "Active" : "Inactive"}
                        sx={{
                          bgcolor: buyer.is_active === 1 ? "#dcfce7" : "#fee2e2",
                          color: buyer.is_active === 1 ? "#047857" : "#b91c1c",
                          fontWeight: 900,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton onClick={() => openEditDialog(buyer)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={buyer.is_active === 1 ? "Mark inactive" : "Mark active"}>
                        <IconButton onClick={() => handleToggleStatus(buyer)} disabled={!buyer.id}>
                          <PowerSettingsNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => handleDelete(buyer)} disabled={!buyer.id}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>
          {editingBuyer ? "Edit Buyer" : "Add Buyer"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.5, mt: 1 }}>
            <TextField
              label="NTN/CNIC"
              value={form.buyer_ntn_cnic}
              onChange={(event) => handleFormChange("buyer_ntn_cnic", event.target.value)}
              fullWidth
            />
            <TextField
              label="Business Name"
              value={form.buyer_business_name}
              onChange={(event) => handleFormChange("buyer_business_name", event.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Sales Tax No"
              value={form.buyer_sales_tax_no}
              onChange={(event) => handleFormChange("buyer_sales_tax_no", event.target.value)}
              fullWidth
            />
            <TextField
              label="Buyer STRN"
              value={form.buyer_strn}
              onChange={(event) => handleFormChange("buyer_strn", event.target.value)}
              fullWidth
            />
            <TextField
              select={provinces.length > 0}
              label="Province"
              value={form.buyer_province}
              onChange={(event) => handleFormChange("buyer_province", event.target.value)}
              fullWidth
            >
              {provinces.map((province) => (
                <MenuItem key={province.stateProvinceCode} value={province.stateProvinceDesc}>
                  {province.stateProvinceDesc}
              </MenuItem>
            ))}
          </TextField>
            <TextField
              label="Address"
              value={form.buyer_address}
              onChange={(event) => handleFormChange("buyer_address", event.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
            />
            <TextField
              select
              label="Registration Type"
              value={form.buyer_registration_type}
              onChange={(event) => handleFormChange("buyer_registration_type", event.target.value)}
              fullWidth
            >
              {registrationTypes.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Terms of Sale"
              value={form.terms_of_sale}
              onChange={(event) => handleFormChange("terms_of_sale", event.target.value)}
              fullWidth
            />
            <TextField
              select
              label="Status"
              value={form.is_active}
              onChange={(event) => handleFormChange("is_active", Number(event.target.value))}
              fullWidth
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeDialog} color="secondary">Cancel</Button>
          <Button variant="contained" onClick={handleSave} sx={{ fontWeight: 900, textTransform: "none" }}>
            Save Buyer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Buyers;
